import React, { useEffect, useState } from "react";
import { Check, Sparkles, Settings2, Loader2 } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const PLAN_LABELS = {
  it: {
    monthly: { title: "Mensile", suffix: "/mese" },
    six_month: { title: "6 mesi", suffix: " / 6 mesi" },
    yearly: { title: "12 mesi", suffix: " / anno" },
    save: "Miglior valore",
    active: "Iscrizione attiva",
    freeLaunch: "Lancio gratuito attivo",
    freeLaunchBody:
      "In questo momento NO VAX LOVE è gratuito per tutti i membri. Nessun pagamento richiesto.",
    manage: "Gestisci iscrizione",
    tagline: "Una sola iscrizione. Tutto incluso.",
    intro:
      "Una sola iscrizione include tutte le funzioni della community. Nessun pagamento aggiuntivo, nessun blocco su funzionalità core.",
    promoLabel: "Prezzo promozionale — lancio",
    stdLabel: "Prezzo standard",
  },
  en: {
    monthly: { title: "Monthly", suffix: "/month" },
    six_month: { title: "6 months", suffix: " / 6 months" },
    yearly: { title: "12 months", suffix: " / year" },
    save: "Best value",
    active: "Membership active",
    freeLaunch: "Free launch in progress",
    freeLaunchBody:
      "NO VAX LOVE is currently free for every member. No payment required.",
    manage: "Manage subscription",
    tagline: "One membership. Everything included.",
    intro:
      "One membership includes every feature of the community. No add-ons, no gates on core dating features.",
    promoLabel: "Promotional launch pricing",
    stdLabel: "Standard pricing",
  },
};

const BENEFITS = {
  it: [
    "Like illimitati",
    "Ricerca, matching e discovery avanzati",
    "Messaggi, note vocali e foto",
    "Sistema di compatibilità completo",
    "Filtri avanzati e community feed",
    "Nessuna funzione a pagamento aggiuntivo",
  ],
  en: [
    "Unlimited likes",
    "Advanced matching, discovery and search",
    "Messages, voice notes and photo attachments",
    "Complete compatibility system",
    "Advanced filters and community feed",
    "No extra paid features",
  ],
};

function formatPrice(cents, currency) {
  const value = (cents || 0) / 100;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: (currency || "eur").toUpperCase(),
    minimumFractionDigits: 2,
  }).format(value);
}

export default function Membership() {
  const { lang, t } = useI18n();
  const { user } = useAuth();
  const [phase, setPhase] = useState(null);
  const [plans, setPlans] = useState([]);
  const [currency, setCurrency] = useState("eur");
  const [ent, setEnt] = useState(null);
  const [providers, setProviders] = useState({ stripe: true, paypal: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [pl, en, prov] = await Promise.all([
          api.get("/billing/plans"),
          api.get("/billing/entitlement"),
          api.get("/billing/providers"),
        ]);
        setPhase(pl.data?.phase);
        setPlans(pl.data?.plans || []);
        setCurrency(pl.data?.currency || "eur");
        setEnt(en.data);
        setProviders(prov.data || { stripe: true, paypal: false });
      } catch (e) {
        setError(extractError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const labels = PLAN_LABELS[lang];

  const startCheckout = async (lookup_key, provider = "stripe") => {
    setError("");
    setBusy(`${provider}:${lookup_key}`);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key,
        provider,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.assign(data.checkout_url);
      } else {
        setError("Missing checkout URL");
        setBusy(null);
      }
    } catch (e) {
      setError(extractError(e));
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      // PayPal has no Stripe-equivalent portal — offer a direct cancel with
      // confirmation. Stripe subscriptions go through the hosted portal.
      if (ent?.plan && (ent?.tier === "promotional" || ent?.tier === "standard") &&
          ent?.source === "subscription" && user?.membership_provider === "paypal") {
        // Confirm cancel
        const ok = window.confirm(lang === "it"
          ? "Vuoi annullare la tua iscrizione PayPal? Rimarrà attiva fino alla fine del periodo corrente."
          : "Cancel your PayPal subscription? It stays active until the end of the current period.");
        if (!ok) return;
        await api.post("/billing/paypal/cancel");
        toast.success(lang === "it" ? "Cancellazione avviata." : "Cancellation requested.");
        window.location.reload();
        return;
      }
      const { data } = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.assign(data.url);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setPortalBusy(false);
    }
  };

  const activeSub = ent?.active && ent?.source === "subscription";
  const activeFreeLaunch = ent?.active && ent?.source === "free_launch";
  const bestValuePlan = plans.find((p) => p.plan_id === "yearly")?.lookup_key;

  return (
    <div className="max-w-5xl" data-testid="page-membership">
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
        {lang === "it" ? "Iscrizione" : "Membership"}
      </p>
      <h1 className="mt-3 font-display text-4xl md:text-5xl">{labels.tagline}</h1>
      <p className="mt-3 text-sm max-w-xl" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {labels.intro}
      </p>

      {/* Active subscription banner */}
      {activeSub ? (
        <div
          className="mt-8 nvl-card p-6 flex items-center justify-between"
          data-testid="membership-active-banner"
          style={{ borderColor: "hsl(var(--nvl-accent))" }}
        >
          <div>
            <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
              {labels.active}
            </p>
            <p className="mt-1 font-display text-2xl">
              {ent?.plan || ""}
            </p>
            {ent?.expires_at ? (
              <p className="text-xs mt-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                {lang === "it" ? "Rinnovo previsto: " : "Renews: "}
                {new Date(ent.expires_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
                  year: "numeric", month: "long", day: "numeric",
                })}
                {ent?.cancel_at_period_end ? (lang === "it" ? " · cancellazione programmata" : " · cancellation scheduled") : ""}
              </p>
            ) : null}
          </div>
          <button
            onClick={openPortal}
            disabled={portalBusy}
            className="nvl-btn nvl-btn-outline text-sm inline-flex items-center gap-2"
            data-testid="membership-open-portal"
          >
            {portalBusy ? <Loader2 size={14} className="animate-spin" /> : <Settings2 size={14} />}
            {labels.manage}
          </button>
        </div>
      ) : null}

      {/* Free-launch banner */}
      {activeFreeLaunch ? (
        <div
          className="mt-8 nvl-card p-8 flex items-start gap-4"
          data-testid="membership-free-launch-banner"
          style={{
            borderColor: "hsl(var(--nvl-accent))",
            backgroundColor: "hsl(var(--nvl-accent) / 0.06)",
          }}
        >
          <Sparkles size={22} style={{ color: "hsl(var(--nvl-accent))" }} />
          <div>
            <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
              {labels.freeLaunch}
            </p>
            <p className="mt-2 font-display text-2xl md:text-3xl">
              {lang === "it" ? "Sei dentro, gratis." : "You're in — free."}
            </p>
            <p className="mt-2 text-sm max-w-xl" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {labels.freeLaunchBody}
            </p>
            {ent?.expires_at ? (
              <p className="mt-2 text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                {lang === "it" ? "Il periodo gratuito termina il " : "Free period ends on "}
                {new Date(ent.expires_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
                  year: "numeric", month: "long", day: "numeric",
                })}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Plans grid — only shown when NOT free_launch */}
      {phase !== "free_launch" && plans.length > 0 ? (
        <div className="mt-10">
          <p className="nvl-overline mb-4" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {phase === "promotional" ? labels.promoLabel : labels.stdLabel}
          </p>
          <div className="grid gap-6 md:grid-cols-3" data-testid="plans-grid">
            {loading ? (
              <div className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                {t("common.loading")}
              </div>
            ) : error ? (
              <div className="text-sm md:col-span-3" data-testid="membership-error"
                   style={{ color: "hsl(var(--destructive))" }}>
                {error}
              </div>
            ) : (
              plans.map((p) => {
                const meta = labels[p.plan_id] || { title: p.plan_id, suffix: "" };
                const isBest = p.lookup_key === bestValuePlan;
                return (
                  <div
                    key={p.lookup_key}
                    className="nvl-card p-8 flex flex-col"
                    data-testid={`plan-card-${p.plan_id}`}
                    style={isBest ? { borderColor: "hsl(var(--nvl-ink))" } : {}}
                  >
                    {isBest ? (
                      <span
                        className="self-start px-3 py-1 rounded-full text-[10px] uppercase tracking-widest"
                        style={{
                          backgroundColor: "hsl(var(--nvl-accent))",
                          color: "hsl(var(--nvl-bg))",
                        }}
                      >
                        {labels.save}
                      </span>
                    ) : null}
                    <h2 className="mt-4 font-display text-3xl">{meta.title}</h2>
                    <div className="mt-3">
                      <span className="font-display text-5xl">
                        {formatPrice(p.amount_cents, p.currency || currency)}
                      </span>
                      <span
                        className="ml-1 text-sm"
                        style={{ color: "hsl(var(--nvl-ink-soft))" }}
                      >
                        {meta.suffix}
                      </span>
                    </div>

                    <ul className="mt-6 space-y-2 flex-1">
                      {BENEFITS[lang].map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Check
                            size={16}
                            style={{ color: "hsl(var(--nvl-accent))" }}
                            className="mt-0.5 shrink-0"
                          />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      className="mt-8 nvl-btn nvl-btn-primary"
                      disabled={!!busy || activeSub}
                      onClick={() => startCheckout(p.lookup_key, "stripe")}
                      data-testid={`plan-choose-${p.plan_id}`}
                    >
                      {busy === `stripe:${p.lookup_key}`
                        ? t("common.loading")
                        : activeSub
                        ? lang === "it"
                          ? "Attivo"
                          : "Active"
                        : lang === "it"
                        ? "Attiva iscrizione"
                        : "Activate membership"}
                    </button>
                    {providers.paypal ? (
                      <button
                        className="mt-3 nvl-btn nvl-btn-outline text-sm"
                        disabled={!!busy || activeSub}
                        onClick={() => startCheckout(p.lookup_key, "paypal")}
                        data-testid={`plan-choose-paypal-${p.plan_id}`}
                      >
                        {busy === `paypal:${p.lookup_key}`
                          ? t("common.loading")
                          : (lang === "it" ? "Paga con PayPal" : "Pay with PayPal")}
                      </button>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : null}

      {phase && phase !== "free_launch" && !activeFreeLaunch ? (
      <p className="mt-10 text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {lang === "it"
          ? "Pagamento sicuro via Stripe. IVA UE calcolata automaticamente al checkout. Puoi annullare in qualsiasi momento dal portale."
          : "Secure payment via Stripe. EU VAT calculated automatically at checkout. Cancel any time from the portal."}
      </p>
      ) : null}
    </div>
  );
}
